import { COMPANY_LEGAL_FIELDS, COMPANY_LEGAL_GROUPS, type CustomFieldDefinition } from '@clim/domain';
import { useEffect, useState } from 'react';
import { ErrorBox, Field, Input, Select, Textarea } from '../../components/ui';
import { useSession } from '../../lib/session';
import { useApi, useMutation } from './common';
import { DocumentsCard, ImageSlot } from './DocumentsCard';

interface TenantSettings {
  name: string;
  planName: string | null;
  limits: Record<string, number | null> | null;
  usage: Record<string, number>;
  settings: {
    legal?: Record<string, string>;
    branding?: { logoUrl?: string };
    clientCustomFields?: CustomFieldDefinition[];
    security?: { mfaRequiredForAdmins?: boolean };
    dossierForm?: { showSaleChannel?: boolean; showProActivity?: boolean };
    pickupOrders?: { supplierVerification?: boolean };
  };
}

/** Configuration par entreprise (CDC §19) : identité, mentions légales, champs métier clients. */
export function SettingsAdmin() {
  const { data, error, reload } = useApi<TenantSettings>('/v1/admin/tenant');
  const { refreshMe } = useSession();
  const m = useMutation();
  const [legal, setLegal] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  useEffect(() => {
    if (!data) return;
    setLegal(data.settings.legal ?? {});
    setFields(data.settings.clientCustomFields ?? []);
  }, [data]);

  const save = async () => {
    await m.run('PATCH', '/v1/admin/tenant', {
      settings: { legal, clientCustomFields: fields },
    });
    await refreshMe();
    reload();
  };
  const setField = (i: number, patch: Partial<CustomFieldDefinition>) =>
    setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  return (
    <div>
      <ErrorBox message={error ?? m.error} />
      {data && (
        <div className="card">
          <h2>Abonnement</h2>
          <p>
            {data.planName ?? '—'} · Superviseurs {data.usage.supervisor ?? 0}/
            {data.limits?.supervisor ?? '∞'} · Commerciaux {data.usage.commercial ?? 0}/
            {data.limits?.commercial ?? '∞'} · Techniciens {data.usage.technician ?? 0}/
            {data.limits?.technician ?? '∞'}
          </p>
        </div>
      )}
      {data && (
        <div className="card form">
          <h2>Sécurité</h2>
          <label className="check">
            <input
              type="checkbox"
              checked={!!data.settings.security?.mfaRequiredForAdmins}
              onChange={(e) =>
                void m
                  .run('PATCH', '/v1/admin/settings/security', { mfaRequiredForAdmins: e.target.checked })
                  .then(reload, () => undefined)
              }
            />{' '}
            Double vérification obligatoire pour les administrateurs
          </label>
          <p className="muted">
            Les comptes qui gèrent l’entreprise, l’équipe ou les droits devront, en plus du mot de passe,
            saisir un code donné par une application d’authentification. Chacun peut aussi l’activer pour
            lui-même (clic sur son prénom → Sécurité du compte).
          </p>
        </div>
      )}
      {data && (
        <div className="card form">
          <h2>Dossiers commerciaux</h2>
          <p className="muted">
            Champs facultatifs du dossier (bloc « Conditions de la vente »). Masqués, ils ne sont ni demandés ni
            bloquants ; les valeurs déjà enregistrées sont conservées et réapparaissent si vous les réactivez.
          </p>
          {(
            [
              ['showSaleChannel', 'Afficher les modalités de conclusion du contrat (« Contrat conclu » : hors établissement, à distance, en agence)'],
              ['showProActivity', 'Afficher l’activité principale et le nombre de salariés du client professionnel'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="check">
              <input
                type="checkbox"
                checked={!!data.settings.dossierForm?.[key]}
                onChange={(e) =>
                  void m
                    .run('PATCH', '/v1/admin/tenant', {
                      settings: {
                        dossierForm: {
                          showSaleChannel: !!data.settings.dossierForm?.showSaleChannel,
                          showProActivity: !!data.settings.dossierForm?.showProActivity,
                          [key]: e.target.checked,
                        },
                      },
                    })
                    .then(async () => {
                      await refreshMe();
                      reload();
                    }, () => undefined)
                }
              />{' '}
              {label}
            </label>
          ))}
        </div>
      )}
      {data && (
        <div className="card form">
          <h2>Bons d’enlèvement fournisseur</h2>
          <p className="muted">
            Quand la vérification est activée, les nouveaux bons portent un QR code et un code que le fournisseur contrôle sur
            une page publique (bon valable, déjà récupéré, annulé ou expiré). Prévenez vos fournisseurs avant de l’activer.
            Les bons déjà émis ne changent pas.
          </p>
          <label className="check">
            <input
              type="checkbox"
              checked={!!data.settings.pickupOrders?.supplierVerification}
              onChange={(e) =>
                void m
                  .run('PATCH', '/v1/admin/tenant', { settings: { pickupOrders: { supplierVerification: e.target.checked } } })
                  .then(async () => {
                    await refreshMe();
                    reload();
                  }, () => undefined)
              }
            />{' '}
            Activer la vérification des bons par le fournisseur (QR code et page de vérification)
          </label>
        </div>
      )}
      <DocumentsCard />
      <div className="card form">
        <h2>Identité</h2>
        {data?.settings.branding?.logoUrl && (
          <img src={data.settings.branding.logoUrl} alt="Logo actuel" className="login-logo" />
        )}
        <p className="muted">
          Votre logo s’affiche en haut de l’application, sur le portail client, sur la page de paiement et sur
          les bons de commande. PNG ou JPEG, 1 Mo maximum.
        </p>
        <ImageSlot
          slot="logo"
          present={
            !!(data?.settings as { documents?: { images?: { logo?: string } } } | undefined)?.documents
              ?.images?.logo
          }
          onChanged={() => void refreshMe().then(reload)}
        />
      </div>
      {(Object.keys(COMPANY_LEGAL_GROUPS) as (keyof typeof COMPANY_LEGAL_GROUPS)[]).map((g) => (
        <div className="card form" key={g}>
          <h2>
            {g === 'identite' ? 'Mentions légales — ' : ''}
            {COMPANY_LEGAL_GROUPS[g]}
          </h2>
          {g === 'identite' && (
            <p className="muted">
              Ces informations alimentent les CGV, le bon de commande, la mention RGPD et le formulaire de
              rétractation (variables des textes juridiques).
            </p>
          )}
          {COMPANY_LEGAL_FIELDS.filter((f) => f.group === g).map((f) => (
            <Field key={f.key} label={`${f.label}${f.required ? ' *' : ''}`}>
              {f.multiline ? (
                <Textarea
                  value={legal[f.key] ?? ''}
                  onChange={(e) => setLegal({ ...legal, [f.key]: e.target.value })}
                />
              ) : (
                <Input
                  value={legal[f.key] ?? ''}
                  onChange={(e) => setLegal({ ...legal, [f.key]: e.target.value })}
                />
              )}
            </Field>
          ))}
        </div>
      ))}
      <div className="actions">
        <button className="btn primary" onClick={() => void save()}>
          Enregistrer
        </button>
      </div>
      <div className="card form">
        <h2>Champs métier des clients</h2>
        {fields.map((f, i) => (
          <div className="grid3" key={i}>
            <Field label="Libellé">
              <Input
                value={f.label}
                onChange={(e) =>
                  setField(i, {
                    label: e.target.value,
                    code:
                      f.code ||
                      e.target.value
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_|_$/g, ''),
                  })
                }
              />
            </Field>
            <Field label="Type">
              <Select
                value={f.type}
                onChange={(e) => setField(i, { type: e.target.value as CustomFieldDefinition['type'] })}
              >
                <option value="text">Texte</option>
                <option value="number">Nombre</option>
                <option value="boolean">Oui / non</option>
                <option value="select">Liste</option>
              </Select>
            </Field>
            <Field label="Pour">
              <Select
                value={f.appliesTo}
                onChange={(e) =>
                  setField(i, { appliesTo: e.target.value as CustomFieldDefinition['appliesTo'] })
                }
              >
                <option value="company">Professionnels</option>
                <option value="individual">Particuliers</option>
                <option value="all">Tous</option>
              </Select>
            </Field>
            {f.type === 'select' && (
              <Field label="Valeurs (séparées par des virgules)">
                <Input
                  value={(f.options ?? []).join(', ')}
                  onChange={(e) =>
                    setField(i, {
                      options: e.target.value
                        .split(',')
                        .map((x) => x.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
            )}
            <label className="check">
              <input
                type="checkbox"
                checked={f.required}
                onChange={(e) => setField(i, { required: e.target.checked })}
              />{' '}
              Obligatoire
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={f.isActive}
                onChange={(e) => setField(i, { isActive: e.target.checked })}
              />{' '}
              Actif
            </label>
          </div>
        ))}
        <button
          type="button"
          className="link"
          onClick={() =>
            setFields([
              ...fields,
              { code: '', label: '', type: 'text', appliesTo: 'company', required: false, isActive: true },
            ])
          }
        >
          + Ajouter un champ
        </button>
      </div>
      <div className="sticky-actions">
        <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
          Enregistrer
        </button>
      </div>
    </div>
  );
}
